import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Brain, 
  Users, 
  Target, 
  Zap, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

/**
 * Composant pour afficher les résultats détaillés d'une qualification IA
 * Affiche: scores par catégorie, niveau, décision, forces, faiblesses, risques
 */
const QualificationResultCard = ({ qualification, compact = false }) => {
  if (!qualification) return null;

  // Récupérer les données
  const {
    score_total = 0,
    score_color = 'gray',
    scores_detail = {},
    niveau = '',
    niveau_label = '',
    niveau_color = 'gray',
    forces = [],
    faiblesses = [],
    risques = [],
    decision = '',
    decision_label = '',
    decision_color = 'gray',
    profile_type = '',
    profile_title = '',
    analyzed_at = ''
  } = qualification;

  // Couleurs par type
  const getColorClass = (color) => {
    switch (color) {
      case 'green': return 'bg-green-500';
      case 'orange': return 'bg-orange-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getTextColorClass = (color) => {
    switch (color) {
      case 'green': return 'text-green-600';
      case 'orange': return 'text-orange-600';
      case 'red': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getBorderColorClass = (color) => {
    switch (color) {
      case 'green': return 'border-green-500';
      case 'orange': return 'border-orange-500';
      case 'red': return 'border-red-500';
      default: return 'border-gray-400';
    }
  };

  const getDecisionIcon = () => {
    switch (decision) {
      case 'A_RECRUTER': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'A_TESTER': return <Clock className="h-5 w-5 text-orange-500" />;
      case 'REJETER': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Minus className="h-5 w-5 text-gray-400" />;
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Icône par catégorie
  const getCategoryIcon = (key) => {
    if (key.includes('cognitif') || key.includes('logique')) return <Brain className="h-4 w-4" />;
    if (key.includes('social') || key.includes('contenu')) return <Users className="h-4 w-4" />;
    if (key.includes('execution')) return <Target className="h-4 w-4" />;
    if (key.includes('mental')) return <Zap className="h-4 w-4" />;
    if (key.includes('resolution') || key.includes('technique')) return <TrendingUp className="h-4 w-4" />;
    if (key.includes('business')) return <TrendingUp className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  // Version compacte (pour afficher dans un badge/summary)
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
        {getDecisionIcon()}
        <div>
          <div className="flex items-center gap-2">
            <span className={`font-bold text-lg ${getTextColorClass(score_color)}`}>
              {score_total}/100
            </span>
            <Badge className={`${getColorClass(decision_color)} text-white text-xs`}>
              {decision_label || decision}
            </Badge>
          </div>
          <p className="text-xs text-gray-500">{profile_title || profile_type}</p>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-tp-border" data-testid="qualification-result-card">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            <CardTitle className="text-base sm:text-lg font-display text-tp-text break-words">
              Analyse IA - {profile_title || profile_type}
            </CardTitle>
            <Badge className={`${getColorClass(decision_color)} text-white whitespace-nowrap`}>
              {decision_label || decision}
            </Badge>
          </div>
          <div className={`self-start sm:self-auto inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border-2 ${getBorderColorClass(score_color)} whitespace-nowrap`}>
            <span className="text-xs sm:text-sm text-gray-500">Score</span>
            <span className={`text-xl sm:text-2xl font-bold ${getTextColorClass(score_color)}`}>
              {score_total}/100
            </span>
          </div>
        </div>
        {analyzed_at && (
          <p className="text-xs text-gray-400 mt-2">Analysé le {formatDate(analyzed_at)}</p>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Niveau global */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          {getDecisionIcon()}
          <div>
            <p className="text-sm text-gray-500">Niveau global</p>
            <p className={`text-lg font-semibold ${getTextColorClass(niveau_color)}`}>
              {niveau_label || niveau || 'Non évalué'}
            </p>
          </div>
        </div>

        {/* Scores détaillés par catégorie */}
        {Object.keys(scores_detail).length > 0 && (
          <div>
            <h4 className="font-medium text-tp-text mb-4 flex items-center gap-2">
              <Target className="h-4 w-4 text-tp-accent" />
              Scores par catégorie
            </h4>
            <div className="space-y-4">
              {Object.entries(scores_detail).map(([key, data]) => {
                // Gérer les deux formats possibles
                const score = typeof data === 'object' ? data.score : data;
                const max = typeof data === 'object' ? data.max : 100;
                const label = typeof data === 'object' ? data.label : key.replace('score_', '').replace(/_/g, ' ');
                const color = typeof data === 'object' ? data.color : (score / max >= 0.75 ? 'green' : score / max >= 0.55 ? 'orange' : 'red');
                const percentage = typeof data === 'object' ? data.percentage : Math.round((score / max) * 100);

                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(key)}
                        <span className="text-gray-600 capitalize">{label}</span>
                      </div>
                      <span className={`font-medium ${getTextColorClass(color)}`}>
                        {score}/{max}
                      </span>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-2"
                      indicatorClassName={getColorClass(color)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Forces, Faiblesses, Risques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Forces */}
          {forces && forces.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Points forts
              </h4>
              <ul className="space-y-2">
                {forces.map((force, index) => (
                  <li key={`force-${force}-${index}`} className="text-sm text-green-600 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{force}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Faiblesses */}
          {faiblesses && faiblesses.length > 0 && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-700 mb-3 flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                Points faibles
              </h4>
              <ul className="space-y-2">
                {faiblesses.map((faiblesse, index) => (
                  <li key={`faiblesse-${faiblesse}-${index}`} className="text-sm text-orange-600 flex items-start gap-2">
                    <Minus className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{faiblesse}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risques */}
          {risques && risques.length > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Risques identifiés
              </h4>
              <ul className="space-y-2">
                {risques.map((risque, index) => (
                  <li key={`risque-${risque}-${index}`} className="text-sm text-red-600 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{risque}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QualificationResultCard;
